import { useEffect, useMemo, useRef, useState } from 'react';
import { useClerk, useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@/lib/icons';
import { getAdminOrgs, createClientOrg } from '@/data/client';
import { useViewAsClient } from '@/auth/viewAsClient';

/* Admin-only account switcher with type-to-filter search. Replaces Clerk's
   built-in OrganizationSwitcher for admins, who belong to every client org and
   need to find one by name rather than scroll a long list. Non-admins keep the
   Clerk switcher (they have only 1–2 orgs). Switching uses Clerk's setActive,
   then reloads so the new org's data loads fresh. */
export function AccountSwitcher() {
  const clerk = useClerk();
  const { organization } = useOrganization();
  const { setActive } = useOrganizationList();
  const { data } = useQuery({ queryKey: ['adminOrgs'], queryFn: getAdminOrgs, staleTime: 5 * 60_000 });

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [, setViewAsClient] = useViewAsClient();
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const orgs = data?.orgs ?? [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (s ? orgs.filter((o) => o.name.toLowerCase().includes(s)) : orgs).slice(0, 50);
  }, [orgs, q]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => { document.removeEventListener('mousedown', onDoc); clearTimeout(t); };
  }, [open]);

  const switchTo = async (orgId: string) => {
    if (!setActive || busy) return;
    setBusy(true);
    try {
      await setActive({ organization: orgId });
      window.location.assign('/');
    } catch {
      setErr('Could not switch account.');
      setBusy(false);
    }
  };

  // Opens Clerk's Organization Profile modal for the active org — rename +
  // add/remove/invite members. Restores the "Manage organization" flow the
  // built-in OrganizationSwitcher used to provide before this custom picker.
  const manage = () => {
    setOpen(false);
    clerk.openOrganizationProfile();
  };

  const create = async () => {
    const name = newName.trim();
    if (!name || !setActive || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await createClientOrg(name);
      await setActive({ organization: r.orgId });
      window.location.assign('/');
    } catch (e) {
      // Surface the server's reason (e.g. a Clerk org limit) rather than a generic message.
      const m = e instanceof Error ? e.message : '';
      const brace = m.indexOf('{');
      let msg = 'Could not create client.';
      if (brace >= 0) { try { msg = JSON.parse(m.slice(brace)).error || msg; } catch { /* keep default */ } }
      setErr(msg);
      setBusy(false);
    }
  };

  return (
    <div className="acct-switch" ref={ref}>
      <button
        type="button"
        className="acct-switch__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="acct-switch__name">{organization?.name ?? 'Select account'}</span>
        <Icon name="chevron-down" size={16} />
      </button>

      {open && (
        <div className="acct-switch__pop">
          <div className="acct-switch__searchwrap">
            <Icon name="search" size={14} />
            <input
              ref={searchRef}
              className="acct-switch__search"
              placeholder="Search clients…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <ul className="acct-switch__list" role="listbox">
            {filtered.map((o) => {
              const active = organization?.id === o.orgId;
              return (
                <li key={o.orgId}>
                  <button
                    type="button"
                    className={`acct-switch__item ${active ? 'is-active' : ''}`}
                    onClick={() => switchTo(o.orgId)}
                    disabled={busy}
                  >
                    <span>{o.name}</span>
                    {active && <Icon name="check" size={14} />}
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && <li className="acct-switch__empty">No matches</li>}
          </ul>

          {organization && (
            <button type="button" className="acct-switch__manage" onClick={manage} disabled={busy}>
              <Icon name="settings" size={14} />
              <span>Manage “{organization.name}” — name &amp; users</span>
            </button>
          )}
          {organization && (
            /* Demo and support: the whole app as a client of this account sees
               it, with a banner and exit above the page. */
            <button type="button" className="acct-switch__manage" onClick={() => { setOpen(false); setViewAsClient(true); }} disabled={busy}>
              <Icon name="eye" size={14} />
              <span>View “{organization.name}” as a client</span>
            </button>
          )}

          <div className="acct-switch__foot">
            <input
              className="acct-switch__newname"
              placeholder="New client name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
              disabled={busy}
            />
            <button
              type="button"
              className="acct-switch__create"
              onClick={create}
              disabled={!newName.trim() || busy}
            >
              <Icon name="plus" size={14} />
              <span>{busy ? 'Working…' : 'Create'}</span>
            </button>
          </div>
          {err && <div className="acct-switch__err">{err}</div>}
        </div>
      )}
    </div>
  );
}
